import { Component } from 'react';
import GameData from "../mobx/GameData";
import ApplicationState from '../mobx/ApplicationState';
import { observe } from "mobx";
import { Button, Card, FloatingLabel, Form, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { Container, Row, Col } from 'react-bootstrap';
import slugify from 'slugify';
import { withAuth0 } from '@auth0/auth0-react';
import { CONSTANTS } from '../config/Constants';
import { DateTime } from 'luxon';

import './SharePage.scss';

// This withAuth0 is extremely frustrating to have to write, but I just don't care enough to write good/consistent code
// anymore. React is insistent on everyone changing to their stupid functional components and everyone else wants to just
// make their components harder to work with because it's a "better integration". I JUST WANT A DAMN FUNCTION THAT I CAN 
// CALL TO OPEN THE LOGIN PAGE. THEN ANOTHER TO FIND OUT THE USER AND GET AN AUTH TOKEN. WHY DO I NEED 50 LAYERS OF
// ABSTRACTION? Seriously, complexity for the sake of complexity isn't pretty. I wish I'd written this whole thing 
// in Vue instead of trying to go with the industry standard. "It'll be better experience", "It'll look great on a resume"
// yeah but it probably added an extra 25%+ to the project build time, and added absolutely nothing of value to the 
// end product. ARGH.
export default withAuth0(class SharePage extends Component {

    gameDataCleanup = null;

    constructor(props) {
        super(props);

        this.state = {
            gameData: JSON.parse(JSON.stringify(GameData.currentGame)),
            link: '/play/' + GameData.currentGame.id + '/' + slugify(GameData.currentGame.title),
            auth0Connected: false,
            previewWindow: null,
            shareInProgress: false
        };

        if (!this.state.gameData.hasBeenMadePublic) {
            this.state.gameData.isPublicShared = true;
        }
    }

    componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
    }

    toggleEnabled(event) {
        this.setState({
            gameData: { 
                ...this.state.gameData,
                isPublicShared: !!event.target.checked
            }
        });
    }

    toggleDownloads(event) {
        this.setState({
            gameData: { 
                ...this.state.gameData,
                romDownloadAllowed: !!event.target.checked,
            }
        });
    }

    updateDescription(event) {
        this.setState({
            gameData: {
                ...this.state.gameData,
                gameDescription: event.target.value,
            }
        });
    }

    async login() {
        try {
            await this.props.auth0.loginWithPopup()
        } catch (e) {
            console.warn('Login failed, user probably cancelled', e);
            return;
        }
        this.forceUpdate();
    }

    async logout() {
        this.props.auth0.logout();
    }

    copyLink() {
        navigator.clipboard.writeText('https://' + window.location.hostname + this.state.link);
        ApplicationState.addAlert('success', 'Link copied to clipboard');
    }

    async saveChanges(event) {

        await new Promise(resolve => this.setState({
            gameData: {
                ...this.state.gameData,
                lastShared: Date.now()
            },
            shareInProgress: true
        }, resolve));

        try {
            const token = await this.props.auth0.getAccessTokenSilently({
                audience: 'https://puzzle-api.nes.science',
                scope: 'modify:game'
            });

            const res = await fetch(`${CONSTANTS.PuzzleApiUrl}/game`, {
                headers: {Authorization: 'Bearer ' + token},
                method: 'POST',
                cache: 'no-cache',
                body: JSON.stringify({
                    game: this.state.gameData
                })
            });
            const result = await res.json();

            if (!result.success) {
                throw new Error('Failed getting data, contents: ' + JSON.stringify(result));
                
            }
        } catch (e) {
            console.warn('Saving game failed', e);
            ApplicationState.addAlert('danger', 'Unable to share game, please try again');
            return;
        }

        await new Promise(resolve => this.setState({gameData: {...this.state.gameData, hasBeenMadePublic: true}, shareInProgress: false}, resolve));
        GameData.replaceCurrentGame(this.state.gameData);
        ApplicationState.addAlert('success', 'Shared (or unshared) game successfully! See the top of the page for a link to the shared page.')
    }

    showPreview() {
        localStorage.setItem('RetroPuzzleMakerV2.CurrentGamePreview', JSON.stringify(this.state.gameData));
        if (this.state.previewWindow === null) {
            this.setState({previewWindow: window.open('/play/preview/current')})
        } else {
            try {
                this.state.previewWindow.location.reload();
            } catch (e) {
                // Window closed, reopen it
                this.setState({previewWindow: null}, () => this.showPreview());
            }
        }
    }

    render() {

        let auth0Card;

        if (this.props.auth0.isLoading) {
            auth0Card = (
                <Card className="auth0-card">
                    <Card.Body>
                        <Card.Title>User Information</Card.Title>
                        <div className="spinner-wrap">
                            <Spinner animation="grow" variant="primary" />
                        </div>

                    </Card.Body>
                </Card>

            )
        } else if (this.props.auth0.isAuthenticated) {
            auth0Card = (
                <Card className="auth0-card">
                    <Card.Body>
                        <Card.Title>User Information</Card.Title>
                        <p><img className="profile" alt="user profile" src={this.props.auth0.user.picture} /></p>
                        <p>You are logged in as {this.props.auth0.user.name}.</p>
                        <p>This game will be linked with your name.</p>

                        <Button variant="secondary" onClick={() => this.logout()}>Log Out</Button>
                    </Card.Body>
                </Card>
            );

        } else {
            auth0Card = (
                <Card className="auth0-card">
                    <Card.Body>
                        <Card.Title>Log In</Card.Title>
                        <p>Please log into an account in order to share. </p>

                        <Button onClick={() => this.login()}>Log In</Button>
                    </Card.Body>
                </Card>
            )
        }

        return (
            <div className="share-page">
                <Container>
                    <Row>
                        <Col className="container-col">

                            <h1>Share</h1>
                            <p className="share-button-area">
                                {this.state.gameData.hasBeenMadePublic ? 
                                    (<>
                                        <Button variant="outline-secondary" disabled={!this.state.gameData.isPublicShared} onClick={() => this.copyLink()}>Copy Link</Button>
                                        {' '}
                                        <a style={{display: 'none'}} rel="noreferrer" target="_blank" href={this.state.link}>game</a>
                                        <Button variant="outline-primary" disabled={!this.state.gameData.isPublicShared} onClick={event => event.target.previousSibling.click()}>Open shared game page</Button>
                                    </>) :
                                    <em><small>Share links not available until shared</small></em>
                                }

                            </p>
                            <p className="mt-3">
                                Sharing your game publishes a <strong>snapshot</strong> of your game to the internet, with a shareable link!
                            </p>
                            <p>
                                If you make additional changes to your game, you have to share again before users see the changes.
                            </p>
                            <p>
                                <small>
                                    Your game will be available to anyone who gets the link to it.
                                </small>
                            </p>
                            <p>
                                Last published: {' '}
                                <small>
                                    {this.state.gameData.lastShared ? (DateTime.fromMillis(this.state.gameData.lastShared)).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY) : <em>Never</em>}
                                </small>
                            </p>
                            <OverlayTrigger placement="top" delay={{ show: 550, hide: 400 }} overlay={
                                <Tooltip>{GameData.currentGame.hasBeenMadePublic ? 'Use this switch to toggle whether your game is visible at the link given.' : 'This game is not yet shared. Once you share it, you will be able to enable and disable sharing with this switch.'}</Tooltip>
                            }>
                                <div className="share-page-toggle">
                                    <Form.Check className="" 
                                        type="checkbox" 
                                        id="enable-disable-share-page" 
                                        label="Make game publicly available" 
                                        onChange={e => this.toggleEnabled(e)} 
                                        defaultChecked={this.state.gameData.isPublicShared}
                                        disabled={!GameData.currentGame.hasBeenMadePublic}
                                    />
                                </div>
                            </OverlayTrigger>

                            <div className="rom-download-toggle mt-1">
                                <OverlayTrigger placement="top" delay={{ show: 550, hide: 400 }} overlay={
                                    <Tooltip>If this is checked, </Tooltip>
                                }>

                                    <Form.Check className=""
                                        type="checkbox" 
                                        id="allow-rom-downloads" 
                                        label="Allow rom downloads" 
                                        onChange={e => this.toggleDownloads(e)} 
                                        defaultChecked={this.state.gameData.romDownloadAllowed}
                                    />
                                </OverlayTrigger>
                            </div>

                            <div>
                                <div className="mt-3">
                                    <p>
                                        Enter a description for your game here. <a target="_blank" rel="noreferrer" href="https://www.markdownguide.org/cheat-sheet/">Markdown</a> is supported. HTML is not.
                                        <Button className="preview-button" variant="outline-info" onClick={() => this.showPreview()}>Show Preview</Button>
                                    </p>

                                    <FloatingLabel controlId="game-description-text" label="Game Description" className="mb-3">
                                        <Form.Control className="game-description" as="textarea" placeholder="Enter a description for your game" defaultValue={this.state.gameData.gameDescription} onChange={event => this.updateDescription(event)} maxLength={8000} />
                                    </FloatingLabel>
                                </div>
                            </div>

                        </Col>
                    </Row>
                    <Row>
                        <Col md="6" sm="12">
                            {auth0Card}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="button-footer-bar">
                                <Button variant="primary" type="submit" disabled={!this.props.auth0.user || this.state.shareInProgress} onClick={event => this.saveChanges(event)}>{this.state.shareInProgress ? 'Updating...' : (this.state.gameData.isPublicShared ? 'Share Game' : 'Unshare Game')}</Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }
})